import { BaseCard } from '@/components/uiKit/Cards';
import React, { useMemo } from 'react';
import { useAppFilters } from '@/hooks/shared';
import { ASSET_STUDIO } from '@/types/shared/navigation-tabs.enums';
import { SENSORS_DATA_TYPE } from '@/const';
import { RootState } from '@/store/store';
import { useSelector } from 'react-redux';
import { isAfter, parseISO } from 'date-fns';
import { useUpdateFeedbackMutation } from '@/store/api/diagnosticApi';
import { UserThumbFeedback } from '@/types/shared/thumb-feedback.types';
import { sortComponentsByHealthStatusAndName } from '@/components/pages/AssetsStudioPage/SidePane/ComponentSelector/util';
import { AccordionSection } from '@/components/uiKit/FiltersGroup/AccordionSection';
import {
  DiagnosticsChevron,
  ItemDetailsContent,
  ItemDetailsHeader,
} from '@/components/pages/AssetsPreviewPage/AssetsOverview/AssetPreviewTabsViews/Faults/components/ItemsDrawerContent';
import { Success } from '@/assets/Icons';
import { Icon } from '@/components/shared/display/Icon';
import { DiagnosticsHeader } from '../../_common/DiagnosticsHeader';
import { DEFAULT_FAULT_TYPE, Fault } from '@/types/models/faults.types';

// DeepPartial type utility
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

/** Builds evidence_data from evidence_data (preferred) or sources_info (fallback).
 *  Always includes camera sources from sources_info even if evidence_data exists. */
function buildEvidenceData(data: any): { text: string; resource_id: string | null; source_type?: string | null }[] {
  const cameraSource = data.sources_info?.find(
    (s: any) => s.source_type?.toLowerCase() === 'camera'
  );
  const cameraEntry = cameraSource
    ? { text: cameraSource.evidence_text, resource_id: cameraSource.evidence_resource_id || null, source_type: 'camera' }
    : null;

  if (data.evidence_data?.length > 0) {
    const hasCamera = data.evidence_data.some((e: any) => e.source_type?.toLowerCase() === 'camera');
    if (hasCamera || !cameraEntry) return data.evidence_data;
    return [...data.evidence_data, cameraEntry];
  }
  const result = data.sources_info
    ?.filter((s: any) => s.evidence_text || s.evidence_resource_id || s.source_type?.toLowerCase() === 'camera')
    .map((s: any) => ({ text: s.evidence_text, resource_id: s.evidence_resource_id || null, source_type: s.source_type || null })) || [];

  // If no entries have actual evidence, return empty to trigger the fallback navigation link
  if (result.length > 0 && !result.some((e: any) => e.text || e.resource_id)) {
    return [];
  }
  return result;
}

interface PartsOverviewProps {
  assetName: string;
  components: any[];
  setViewType: React.Dispatch<React.SetStateAction<string>>;
  isLoading: boolean;
}

{
  /* @TODO: break to seperate components */
}
export const CauseAndActionSection = ({ setViewType, components, isLoading }: PartsOverviewProps) => {
  const { selectedComponent } = useAppFilters();
  const { selectedComponents, selectedDiagnostic, useFaultsField } = useSelector(
    (state: RootState) => state.assetsStudio
  );
  const [updateFeedback, { isLoading: isUpdating }] = useUpdateFeedbackMutation();

  const handleThumbClick = (feedback: UserThumbFeedback) => {
    updateFeedback(feedback);
  };

  const diagnostics = useMemo(() => {
    // console.log('🏭 Fixed Asset - Using:', useFaultsField ? 'NEW FAULTS' : 'OLD DIAGNOSTICS');
    const selectedAsset = selectedDiagnostic?.[1]?.assets?.[0];

    // NEW IMPLEMENTATION - Use faults field + healthy components
    if (useFaultsField) {
      // console.log('✨ Using faults field:', selectedAsset?.faults);

      if (selectedComponent === ASSET_STUDIO.ALL_COMPONENTS) {
        const allDiagnostics = [];

        // Add components with faults
        if (selectedAsset?.faults && selectedAsset.faults.length > 0) {
          const faultDiagnostics = selectedAsset.faults.map((fault: any) => ({
            diagnostic_id: fault.diagnostic_ids?.[0],
            fault_type: fault.fault_type ?? DEFAULT_FAULT_TYPE,
            health_status: fault.health_status,
            diagnostic_created_at: fault.diagnostic_created_at,
            // Backend should provide possible_causes in faults field
            possible_causes:
              fault.possible_causes?.map((cause: any) =>
                typeof cause === 'string' ? { description: cause, feedback: 'no_feedback' } : cause
              ) || [],
            // Transform prescriptive_actions to object format if needed
            prescriptive_actions:
              fault.prescriptive_actions?.map((action: any) =>
                typeof action === 'string' ? { description: action, feedback: 'no_feedback' } : action
              ) || [],
            sources_info: fault.sources_info || [],
            evidence_data: fault.evidence_data || [],
            component: {
              component_id: fault.component_id,
              component_name: fault.component_name,
              unit_ui_name: fault.unit_ui_name,
            },
            isFromFaultsField: true,
          }));
          allDiagnostics.push(...faultDiagnostics);
        }

        // Add healthy/NA components (those not in faults)
        const componentsWithFaults = new Set(selectedAsset?.faults?.map((f: any) => f.component_id) || []);
        const healthyComponents =
          selectedAsset?.components?.filter((comp: any) => !componentsWithFaults.has(comp.component_id)) || [];

        const healthyDiagnostics = healthyComponents.flatMap(
          (comp: any) =>
            comp.diagnostics?.map((diag: any) => ({
              ...diag,
              component: {
                component_id: comp.component_id,
                component_name: comp.component_name || comp.component_ui_name,
                unit_ui_name: comp.unit_ui_name,
              },
              isFromFaultsField: true,
            })) || []
        );
        allDiagnostics.push(...healthyDiagnostics);

        return sortComponentsByHealthStatusAndName(allDiagnostics);
      } else {
        // For single component view
        const componentFaults =
          selectedAsset?.faults?.filter((fault: any) => fault.component_id === selectedComponent) || [];

        if (componentFaults.length > 0) {
          // Component has faults
          return componentFaults.map((fault: any) => ({
            diagnostic_id: fault.diagnostic_ids?.[0],
            fault_type: fault.fault_type,
            health_status: fault.health_status,
            diagnostic_created_at: fault.diagnostic_created_at,
            // Backend should provide possible_causes in faults field
            possible_causes:
              fault.possible_causes?.map((cause: any) =>
                typeof cause === 'string' ? { description: cause, feedback: 'no_feedback' } : cause
              ) || [],
            // Transform prescriptive_actions to object format if needed
            prescriptive_actions:
              fault.prescriptive_actions?.map((action: any) =>
                typeof action === 'string' ? { description: action, feedback: 'no_feedback' } : action
              ) || [],
            sources_info: fault.sources_info || [],
            evidence_data: fault.evidence_data || [],
            component: {
              component_id: fault.component_id,
              component_name: fault.component_name,
              unit_ui_name: fault.unit_ui_name,
            },
            isFromFaultsField: true,
          }));
        } else {
          // Component is healthy/NA - get from components
          const comp = selectedAsset?.components?.find((c: any) => c.component_id === selectedComponent);
          if (comp) {
            return (
              comp.diagnostics?.map((diag: any) => ({
                ...diag,
                component: {
                  component_id: comp.component_id,
                  component_name: comp.component_name || comp.component_ui_name,
                  unit_ui_name: comp.unit_ui_name,
                },
                isFromFaultsField: true,
              })) || []
            );
          }
        }
      }
    }

    // OLD IMPLEMENTATION - Use diagnostics from components
    if (selectedComponent === ASSET_STUDIO.ALL_COMPONENTS) {
      // up to Apr 7th 2024 there is no video data, only pics
      const noVideoSupportDate = parseISO('2024-04-08');
      // Use components from selectedAsset instead of prop
      const componentsToUse = selectedAsset?.components || components;
      const allDiagnostics = componentsToUse?.flatMap((comp: any) => {
        const field = comp?.diagnostics ? 'diagnostics' : 'subcomponents';
        if (field === 'diagnostics')
          return (
            comp?.[field]?.map((el: any) => ({
              hasVideoSupport: isAfter(parseISO(el.diagnostic_created_at), noVideoSupportDate),
              ...el,
              component: {
                component_id: comp.component_id,
                component_name: comp.component_name || comp.component_ui_name,
                unit_ui_name: comp.unit_ui_name,
              },
            })) || []
          );
        else {
          return (
            comp?.[field]?.diagnostics?.map((el: any) => ({
              ...el,
              component: {
                component_id: comp.subcomponent_id,
                component_name: comp.subcomponent_name,
                unit_ui_name: comp.unit_ui_name,
              },
            })) || []
          );
        }
      });
      // Sort the diagnostics after extracting them
      const sortedDiagnostics = sortComponentsByHealthStatusAndName(allDiagnostics || []);
      return sortedDiagnostics || [];
    }

    const comp = selectedDiagnostic?.[1]?.assets?.[0]?.components?.find(
      (el: any) => el.component_id === selectedComponent
    );

    const diagnosticsData =
      selectedDiagnostic && selectedComponents.id && comp ? comp.diagnostics || comp.subcomponent : [];
    // Sort the diagnostics for selected component too
    return sortComponentsByHealthStatusAndName(diagnosticsData);
  }, [components, selectedComponents, selectedComponent, selectedDiagnostic, useFaultsField]);

  return (
    <BaseCard
      variant="wrapper"
      title={
        <DiagnosticsHeader
          selectedComponent={selectedComponent}
          selectedDiagnostic={selectedDiagnostic}
          diagnostics={diagnostics}
          selectedComponents={selectedComponents}
        />
      }
      classNames={{
        card: 'min-h-0 flex flex-col h-full  ',
        content: 'h-full flex w-full flex-1 bg-gray-200 gap-2 overflow-auto scrollbar-thin p-0',
      }}
    >
      <div className="flex flex-col w-full gap-2">
        {/* console.log('📊 Rendering diagnostics:', diagnostics) */}
        {(() => {
          if (!diagnostics || diagnostics.length === 0) {
            return (
              <div className="text-center text-gray-500 p-4">
                {useFaultsField ? 'No faults data available' : 'No diagnostics data available'}
              </div>
            );
          }

          // Filter out healthy and N/A diagnostics
          const faultDiagnostics = diagnostics.flat().filter((diagnostic: any) => {
            const { fault_type, health_status } = diagnostic || {};
            const isHealthyOrNA = health_status === 'healthy' || health_status === 'na';
            const hasNoFaultType = !fault_type || fault_type === 'fault_type';
            // Only show if it has a real fault (not healthy/NA or has a valid fault_type)
            return !(isHealthyOrNA && hasNoFaultType);
          });

          if (faultDiagnostics.length === 0) {
            // Get component name from first diagnostic for empty state message
            const firstDiagnostic = diagnostics.flat()[0];
            const componentName =
              firstDiagnostic?.component?.component_ui_name ||
              firstDiagnostic?.component?.component_name ||
              'Component';
            const isHealthy = firstDiagnostic?.health_status === 'healthy';

            return (
              <BaseCard
                variant="base"
                classNames={{
                  card: 'w-full h-full rounded-lg',
                  content: 'flex items-center flex-col items-center justify-center text-center p-8 gap-2 h-full',
                }}
              >
                <>
                  <Icon icon={Success} className="w-14 h-14" />
                  <div className="text-lg font-semibold text-[#01152B]">No Faults Detected</div>
                  <div className="text-[#55687D] font-medium">
                    {isHealthy
                      ? `${componentName} is healthy and operating normally`
                      : `No diagnostics data available for ${componentName}`}
                  </div>
                </>
              </BaseCard>
            );
          }

          return faultDiagnostics.map((diagnostic: any, index: number) => {
            const {
              diagnostic_id,
              fault_type,
              health_status,
              diagnostic_created_at,
              possible_causes,
              prescriptive_actions,
              sources_info,
            } = diagnostic || {};
            const { component_name, component_ui_name, unit_ui_name } =
              diagnostic?.component || selectedComponents?.data?.[0] || {};

            // Get asset name from selectedDiagnostic (same as DiagnosticsHeader)
            const assetFromDiagnostic = selectedDiagnostic?.[1]?.assets?.[0];
            const assetUiName =
              assetFromDiagnostic?.asset_ui_name || assetFromDiagnostic?.asset_name || unit_ui_name || '';

            // Check if this diagnostic has camera data
            const diagnosticHasCamera = sources_info?.some(
              (src: any) => src.source_type?.toLowerCase() === SENSORS_DATA_TYPE.Camera
            );

            const faultItem: DeepPartial<Fault> = {
              fault_type: {
                name: fault_type ?? '',
              },
              health_status: health_status,
              location: {
                asset_id: assetFromDiagnostic?.asset_id,
                asset_ui_name: assetUiName,
                component_id: diagnostic?.component?.component_id,
                component_ui_name: component_ui_name || component_name || '',
              },

              work_orders: [],
              evidence: {
                evidence_data: buildEvidenceData(diagnostic),
                possible_causes: possible_causes?.map((c: any) => c.description) || [],
                prescriptive_actions: prescriptive_actions?.map((a: any) => a.description) || [],
              },
            };

            return (
              <div key={`${diagnostic_id || index}-${selectedComponent}`}>
                <AccordionSection
                  variant="card"
                  defaultOpen={faultDiagnostics?.length === 1}
                  chevronPosition="left"
                  label={
                    <div className="flex flex-1 w-full px-6 pl-0 items-start">
                      <ItemDetailsHeader
                        item={faultItem}
                        showAssetLink={false}
                        showComponentLink={false}
                        hasCamera={diagnosticHasCamera}
                      />
                    </div>
                  }
                  customChevron={<DiagnosticsChevron />}
                  showViewDetailsText
                  content={<ItemDetailsContent item={faultItem as Partial<Fault>} hasCamera={diagnosticHasCamera} />}
                />
              </div>
            );
          });
        })()}
      </div>
    </BaseCard>
  );
};
